
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { BrainCircuit, FileText, MessageSquare, Video, FileVideo } from "lucide-react";

interface AIFeaturesProps {
  isHost: boolean;
  isRecording: boolean;
}

const AIFeatures: React.FC<AIFeaturesProps> = ({ isHost, isRecording }) => {
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(true);
  const [summaryEnabled, setSummaryEnabled] = useState(true);
  const [actionItemsEnabled, setActionItemsEnabled] = useState(true);
  const [highlightsEnabled, setHighlightsEnabled] = useState(true);
  
  // Mock data
  const transcript = [
    { time: "00:01:15", speaker: "Alex Johnson", text: "Welcome to our annual tech conference! Today we'll be covering the latest innovations in AI and machine learning." },
    { time: "00:02:30", speaker: "Sarah Williams", text: "I'm excited to share our team's progress on the new natural language processing models we've been developing." },
    { time: "00:03:45", speaker: "Michael Chen", text: "We should also discuss how these technologies can be applied in real-world scenarios for our clients." },
    { time: "00:04:20", speaker: "Alex Johnson", text: "Great point, Michael. Let's dedicate some time to discussing practical applications." },
  ];
  
  const summary = "This meeting covered the introduction to the annual tech conference, with a focus on AI and machine learning innovations. Sarah presented updates on the team's natural language processing models, and the group agreed to discuss practical applications for clients. Key decisions included scheduling a follow-up meeting next week to review progress.";
  
  const actionItems = [
    { assigned: "Alex Johnson", text: "Schedule follow-up meeting for next week" },
    { assigned: "Sarah Williams", text: "Share technical documentation on NLP models with the team" },
    { assigned: "Michael Chen", text: "Create case study examples for client applications" },
    { assigned: "All", text: "Review meeting recording and provide feedback on the presentation" },
  ];
  
  if (!isHost && !isRecording) {
    return (
      <div className="text-center py-8">
        <BrainCircuit className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium mb-2">AI Features Not Available</h3>
        <p className="text-sm text-gray-500 mb-4">
          AI features are only available to hosts or when recording is active.
        </p>
        <p className="text-sm text-gray-500">
          Ask the host to enable recording to use AI features like transcription and summaries.
        </p>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">AI Meeting Assistant</h3>
      </div>
      
      {isHost && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle>AI Features Settings</CardTitle>
            <CardDescription>Enable or disable AI features for this meeting</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <h4 className="text-sm font-medium">Live Transcription</h4>
                  <p className="text-xs text-muted-foreground">Generate real-time transcripts</p>
                </div>
                <Switch 
                  checked={transcriptionEnabled} 
                  onCheckedChange={setTranscriptionEnabled} 
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <h4 className="text-sm font-medium">Meeting Summary</h4>
                  <p className="text-xs text-muted-foreground">Create AI-powered summaries</p>
                </div>
                <Switch 
                  checked={summaryEnabled} 
                  onCheckedChange={setSummaryEnabled} 
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <h4 className="text-sm font-medium">Action Items</h4>
                  <p className="text-xs text-muted-foreground">Extract action items and assignments</p>
                </div>
                <Switch 
                  checked={actionItemsEnabled} 
                  onCheckedChange={setActionItemsEnabled} 
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <h4 className="text-sm font-medium">Video Highlights</h4>
                  <p className="text-xs text-muted-foreground">Generate video highlights after meeting</p>
                </div>
                <Switch 
                  checked={highlightsEnabled} 
                  onCheckedChange={setHighlightsEnabled} 
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {isRecording ? (
        <Tabs defaultValue="transcript" className="flex-grow flex flex-col">
          <TabsList className="grid grid-cols-4">
            <TabsTrigger value="transcript">
              <FileText className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Transcript</span>
            </TabsTrigger>
            <TabsTrigger value="summary">
              <MessageSquare className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Summary</span>
            </TabsTrigger>
            <TabsTrigger value="actions">
              <BrainCircuit className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Actions</span>
            </TabsTrigger>
            <TabsTrigger value="highlights">
              <FileVideo className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Highlights</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="transcript" className="flex-grow overflow-auto mt-4">
            <div className="space-y-4">
              {transcript.map((entry, index) => (
                <div key={index} className="flex space-x-2">
                  <div className="w-16 text-xs text-gray-500 flex-shrink-0">{entry.time}</div>
                  <div>
                    <div className="text-sm font-medium">{entry.speaker}</div>
                    <div className="text-sm">{entry.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="summary" className="flex-grow overflow-auto mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Meeting Summary</CardTitle>
                <CardDescription>Generated as the meeting progresses</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{summary}</p>
              </CardContent>
              <CardFooter>
                <Button variant="outline" size="sm">Export Summary</Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="actions" className="flex-grow overflow-auto mt-4">
            <div className="space-y-2">
              {actionItems.map((item, index) => (
                <div key={index} className="flex items-start p-3 border rounded-md">
                  <input type="checkbox" className="mt-1 mr-3" />
                  <div>
                    <div className="text-sm">{item.text}</div>
                    <div className="text-xs text-gray-500">Assigned to: {item.assigned}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="highlights" className="flex-grow overflow-auto mt-4">
            <div className="text-center py-12">
              <Video className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium mb-2">Video Highlights</h3>
              <p className="text-sm text-gray-500 mb-4">
                Video highlights will be generated after the meeting ends.
              </p>
              <p className="text-sm text-gray-500">
                AI will identify key moments and create a highlight reel automatically.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="text-center py-12 flex-grow">
          <BrainCircuit className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium mb-2">Recording Not Active</h3>
          <p className="text-sm text-gray-500 mb-4">
            AI features are available when recording is active.
          </p>
          {isHost ? (
            <Button onClick={() => {}} variant="outline">
              Start Recording
            </Button>
          ) : (
            <p className="text-sm text-gray-500">
              Ask the host to start recording to use AI features.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AIFeatures;
